import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Plus, Search, AlertTriangle, Upload, CheckSquare, Trash2, FileSpreadsheet, MoreHorizontal, ChevronLeft, ChevronRight, Filter, X, ArrowUp, ArrowDown, ScanBarcode, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ImportDialog from '@/components/ImportDialog';

const typeLabels = {
  raw_material: 'Hammadde', semi_finished: 'Yarı Mamul',
  finished: 'Mamul', consumable: 'Sarf Malzeme', fixed_asset: 'Demirbaş',
  guest_consumption: 'Misafir Tüketim', trade_goods: 'Ticari Mal', other: 'Diğer'
};

const typeColors = {
  raw_material: 'bg-orange-100 text-orange-800',
  semi_finished: 'bg-green-100 text-green-800',
  finished: 'bg-blue-100 text-blue-800',
  consumable: 'bg-purple-100 text-purple-800',
  fixed_asset: 'bg-gray-200 text-gray-700',
  guest_consumption: 'bg-red-100 text-red-800',
  trade_goods: 'bg-cyan-100 text-cyan-800',
  other: 'bg-gray-100 text-gray-600'
};

const productImportColumns = [
  { key: 'code', label: 'Kod' },
  { key: 'name', label: 'Urun Adi' },
  { key: 'type', label: 'Tur' },
  { key: 'category', label: 'Kategori' },
  { key: 'subcategory', label: 'Alt Kategori' },
  { key: 'unit', label: 'Birim', editable: true },
];

export default function ProductList() {
  // Stok miktarı `stock_quantity` iznine bağlı; yetkisi olmayanda
  // sunucu zaten değeri göndermez, sütun da gizlenir.
  const { hasPermission } = useAuth();
  const stokGorebilir = hasPermission('stock_quantity', 'view');
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const silmeYetkisi = hasPermission('products', 'delete');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [typeFilter, setTypeFilter] = useState([]);
  const [catFilter, setCatFilter] = useState([]);
  const [subcatFilter, setSubcatFilter] = useState([]);
  const [activeFilter, setActiveFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);

  // Bulk selection
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState('code');
  const [sortDir, setSortDir] = useState('asc');

  // Filter options from ALL products (not just current page)
  const [filterOptions, setFilterOptions] = useState({ categories: [], subcategories: [], types: [] });

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  // Fetch filter options (all unique values across entire dataset)
  useEffect(() => {
    const params = {};
    if (activeFilter !== 'all') params.is_active = activeFilter === 'active' ? 'true' : 'false';
    api.get('/products/filter-options', { params }).then(r => setFilterOptions(r.data)).catch(() => {});
  }, [activeFilter]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const availableCategories = filterOptions.categories || [];
  const availableSubcats = (filterOptions.subcategories || []).filter(c => catFilter.length === 0 || catFilter.includes(c.parent_id));
  const availableTypes = filterOptions.types || [];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir };
      if (activeFilter !== 'all') params.is_active = activeFilter === 'active' ? 'true' : 'false';
      if (typeFilter.length > 0) params.type = typeFilter.join(',');
      if (catFilter.length > 0) params.category_id = catFilter.join(',');
      if (subcatFilter.length > 0) params.subcategory_id = subcatFilter.join(',');
      if (searchDebounced) params.search = searchDebounced;
      const res = await api.get('/products', { params });
      setProducts(res.data.items || res.data);
      setTotal(res.data.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [typeFilter, catFilter, subcatFilter, searchDebounced, page, perPage, sortBy, sortDir, activeFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { setSubcatFilter([]); }, [catFilter]);
  useEffect(() => { setPage(1); }, [typeFilter, catFilter, subcatFilter, searchDebounced, perPage, activeFilter]);

  // Reset selection when leaving select mode or filters change
  useEffect(() => { setSelected(new Set()); }, [selectMode, searchDebounced, typeFilter, catFilter, subcatFilter]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  // filtered = products (backend handles filtering now)
  const filtered = products;

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    const count = selected.size;
    if (!window.confirm(`${count} urun silinecek. Emin misiniz?`)) return;
    try {
      const ids = Array.from(selected);
      await Promise.all(ids.map(id => api.delete(`/products/${id}`)));
      toast.success(`${count} urun silindi`);
      setSelected(new Set());
      setSelectMode(false);
      fetchProducts();
    } catch (err) { toast.error('Silme hatasi'); }
  };

  const handleBulkExport = async () => {
    try {
      const ids = Array.from(selected);
      const res = await api.post('/products/export', { ids }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `urunler_${selected.size}_adet.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${selected.size} urun Excel olarak indirildi`);
    } catch (err) { toast.error('Export hatasi'); }
  };

  const handleBulkExportForUpdate = async () => {
    try {
      const ids = Array.from(selected);
      const res = await api.post('/products/export', { ids, for_update: true }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'urunler_guncelleme.xlsx'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Guncelleme dosyasi indirildi. Degisiklikleri yapin ve "Toplu Guncelle" ile yukleyin.');
    } catch (err) { toast.error('Export hatasi'); }
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;
  const someSelected = selected.size > 0;

  return (
    <div data-testid="product-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Urun Kartlari</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{total} urun</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowImport(true)} variant="outline" size="sm"
            className="border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0]" data-testid="import-products-btn">
            <Upload size={14} className="mr-1.5" /> Ice Aktar
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm"
                className="border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0]" data-testid="bulk-operations-btn">
                <CheckSquare size={14} className="mr-1.5" /> Toplu Islemler
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#faf7f2] border-[#d0c4b0]">
              <DropdownMenuItem onClick={() => setSelectMode(!selectMode)}
                className="text-[#3F3E39] focus:bg-[#e8dfd0] cursor-pointer" data-testid="toggle-select-mode">
                <CheckSquare size={14} className="mr-2" /> {selectMode ? 'Secim Modundan Cik' : 'Secim Moduna Gir'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                if (selectMode && selected.size > 0) {
                  handleBulkExportForUpdate();
                } else {
                  toast.info('Guncelleme icin once urun secin', { description: 'Secim moduna girip urunleri secerek Excel indirebilirsiniz.' });
                  setSelectMode(true);
                }
              }} className="text-[#3F3E39] focus:bg-[#e8dfd0] cursor-pointer" data-testid="bulk-update-export-menu">
                <RefreshCw size={14} className="mr-2" /> Secili Urunleri Guncelle {selected.size > 0 && `(${selected.size})`}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowUpdate(true)}
                className="text-[#3F3E39] focus:bg-[#e8dfd0] cursor-pointer" data-testid="bulk-update-upload-menu">
                <Upload size={14} className="mr-2" /> Guncelleme Dosyasi Yukle
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/erp/products/new">
            <Button className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" size="sm" data-testid="new-product-btn">
              <Plus size={14} className="mr-1.5" /> Yeni Urun
            </Button>
          </Link>
        </div>
      </div>

      {/* Bulk action bar - selection mode active */}
      {selectMode && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-[#2e2d29] rounded-lg animate-fade-in" data-testid="bulk-action-bar">
          <span className="text-sm text-[#f5f0e8] font-medium">
            {someSelected ? `${selected.size} urun secildi` : 'Islem yapmak icin urun secin'}
          </span>
          <div className="flex-1" />
          {someSelected && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-[#f5f0e8] text-[#1a1916] hover:bg-[#e8dfd0] font-semibold" data-testid="bulk-action-btn">
                  <MoreHorizontal size={14} className="mr-1.5" /> Aksiyonlar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#faf7f2] border-[#d0c4b0]">
                <DropdownMenuItem onClick={() => {
                    const ids = Array.from(selected);
                    window.open(`/barkod?ids=${ids.join(',')}`, '_blank');
                  }} className="text-[#3F3E39] focus:bg-[#e8dfd0] cursor-pointer" data-testid="bulk-barcode-action">
                  <ScanBarcode size={14} className="mr-2" /> Barkod Tanimla ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleBulkExportForUpdate} className="text-[#3F3E39] focus:bg-[#e8dfd0] cursor-pointer" data-testid="bulk-update-export-action">
                  <RefreshCw size={14} className="mr-2" /> Guncelleme Icin Excel Indir ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleBulkExport} className="text-[#3F3E39] focus:bg-[#e8dfd0] cursor-pointer" data-testid="bulk-export-action">
                  <FileSpreadsheet size={14} className="mr-2" /> Disari Aktar ({selected.size})
                </DropdownMenuItem>
                {silmeYetkisi && (
                <DropdownMenuItem onClick={handleBulkDelete} className="text-[#7a3535] focus:bg-[#7a3535]/10 cursor-pointer" data-testid="bulk-delete-action">
                  <Trash2 size={14} className="mr-2" /> Sil ({selected.size})
                </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button size="sm" variant="ghost" onClick={() => { setSelectMode(false); setSelected(new Set()); }}
            className="text-[#f5f0e8]/70 hover:text-[#f5f0e8] hover:bg-[#f5f0e8]/10" data-testid="cancel-select-mode">
            Iptal
          </Button>
        </div>
      )}

      {/* Search + Active/Passive filter */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input placeholder="Urun ara (kod, ad, barkod)..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#d0c4b0]" data-testid="product-search-input" />
        </div>
        <div className="flex bg-[#e8dfd0] rounded-md p-0.5">
          {[{ value: 'active', label: 'Aktif' }, { value: 'passive', label: 'Pasif' }, { value: 'all', label: 'Tumu' }].map(opt => (
            <button key={opt.value} onClick={() => setActiveFilter(opt.value)}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${activeFilter === opt.value ? 'bg-[#faf7f2] text-[#1a1916] shadow-sm' : 'text-[#8a7e70] hover:text-[#1a1916]'}`}
              data-testid={`filter-${opt.value}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              {selectMode && (
                <TableHead className="w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                </TableHead>
              )}
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">
                <button onClick={() => toggleSort('code')} className={`text-xs font-bold uppercase cursor-pointer hover:text-[#3d6e4e] ${sortBy === 'code' ? 'text-[#3d6e4e]' : ''}`}>
                  Kod {sortBy === 'code' && (sortDir === 'asc' ? <ArrowUp size={10} className="inline" /> : <ArrowDown size={10} className="inline" />)}
                </button>
              </TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">
                <button onClick={() => toggleSort('name')} className={`text-xs font-bold uppercase cursor-pointer hover:text-[#3d6e4e] ${sortBy === 'name' ? 'text-[#3d6e4e]' : ''}`}>
                  Urun Adi {sortBy === 'name' && (sortDir === 'asc' ? <ArrowUp size={10} className="inline" /> : <ArrowDown size={10} className="inline" />)}
                </button>
              </TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">
                <MultiFilter label="Kategori" sortKey="category_id" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}
                  options={availableCategories.map(c => ({ value: c.id, label: c.name }))}
                  selected={catFilter} onChange={setCatFilter} testId="product-category-filter" />
              </TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">
                <MultiFilter label="Alt Kategori" sortKey="subcategory_id" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}
                  options={availableSubcats.map(c => ({ value: c.id, label: c.name }))}
                  selected={subcatFilter} onChange={setSubcatFilter} disabled={catFilter.length === 0} testId="product-subcategory-filter" />
              </TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">
                <MultiFilter label="Tur" sortKey="type" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}
                  options={availableTypes}
                  selected={typeFilter} onChange={setTypeFilter} testId="product-type-filter" />
              </TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">
                <button onClick={() => toggleSort('unit')} className={`text-xs font-bold uppercase cursor-pointer hover:text-[#3d6e4e] ${sortBy === 'unit' ? 'text-[#3d6e4e]' : ''}`}>
                  Birim {sortBy === 'unit' && (sortDir === 'asc' ? <ArrowUp size={10} className="inline" /> : <ArrowDown size={10} className="inline" />)}
                </button>
              </TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider">Barkod</TableHead>
              {stokGorebilir && <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider text-right">
                <button onClick={() => toggleSort('total_stock')} className={`text-xs font-bold uppercase cursor-pointer hover:text-[#3d6e4e] ${sortBy === 'total_stock' ? 'text-[#3d6e4e]' : ''}`}>
                  Stok {sortBy === 'total_stock' && (sortDir === 'asc' ? <ArrowUp size={10} className="inline" /> : <ArrowDown size={10} className="inline" />)}
                </button>
              </TableHead>}
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase tracking-wider text-right">
                <button onClick={() => toggleSort('last_purchase_price')} className={`text-xs font-bold uppercase cursor-pointer hover:text-[#3d6e4e] ${sortBy === 'last_purchase_price' ? 'text-[#3d6e4e]' : ''}`}>
                  Son Fiyat {sortBy === 'last_purchase_price' && (sortDir === 'asc' ? <ArrowUp size={10} className="inline" /> : <ArrowDown size={10} className="inline" />)}
                </button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => {
              const isLow = p.min_stock_level && p.total_stock < p.min_stock_level;
              const isSelected = selected.has(p.id);
              return (
                <TableRow key={p.id} className={`erp-table-row border-[#d0c4b0] ${isSelected ? 'bg-[#2e2d29]/5' : ''}`}>
                  {selectMode && (
                    <TableCell>
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(p.id)} data-testid={`select-${p.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-sm text-[#3F3E39]">{p.code}</TableCell>
                  <TableCell>
                    <Link to={`/erp/products/${p.id}`} className="text-[#1a1916] font-medium hover:underline" data-testid={`product-link-${p.id}`}>
                      {p.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm text-[#3F3E39]">{p.category_name || '-'}</TableCell>
                  <TableCell className="text-sm text-[#8a7e70]">
                    {p.subcategory_name || '-'}
                    {p.sinif_name && <span className="block text-[11px] text-[#a3968a]">{p.sinif_name}</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`status-badge ${typeColors[p.type] || ''}`}>{typeLabels[p.type] || p.type}</span>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#e8dfd0] text-[#2e2d29]">{p.unit}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-[#8a7e70]">{p.barcode || '-'}</TableCell>
                  {stokGorebilir && <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isLow && <AlertTriangle size={14} className="text-[#7a3535]" />}
                      <span className={`font-semibold ${isLow ? 'text-[#7a3535]' : 'text-[#1a1916]'}`}>
                        {p.total_stock?.toFixed(1) ?? '0'}
                      </span>
                    </div>
                  </TableCell>}
                  <TableCell className="text-right font-mono text-sm text-[#3F3E39]">
                    {p.last_purchase_price ? `${p.last_purchase_price.toFixed(2)} TL` : '-'}
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={selectMode ? 12 : 11} className="text-center py-8 text-[#8a7e70]">
                  {loading ? 'Yukleniyor...' : 'Urun bulunamadi'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-[#8a7e70]">
            <span>Sayfa basina:</span>
            <Select value={String(perPage)} onValueChange={(v) => setPerPage(Number(v))}>
              <SelectTrigger className="w-20 h-8 text-sm bg-white border-[#d0c4b0]" data-testid="per-page-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
                <SelectItem value="1000">1000</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#8a7e70]">
              {Math.min((page - 1) * perPage + 1, total)}-{Math.min(page * perPage, total)} / {total}
            </span>
            <Button variant="outline" size="icon" className="h-8 w-8 border-[#d0c4b0]"
              disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
              <ChevronLeft size={14} />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8 border-[#d0c4b0]"
              disabled={page * perPage >= total} onClick={() => setPage(p => p + 1)} data-testid="next-page">
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}

      <ImportDialog open={showImport} onOpenChange={setShowImport} config={{
        title: 'Urun Kartlari Ice Aktar',
        templateUrl: '/api/erp/import/template/products',
        previewUrl: '/import/products/preview',
        executeUrl: '/import/products/execute',
        approveCategoriesUrl: '/import/products/approve-categories',
        columns: productImportColumns,
        onComplete: fetchProducts,
        isProductImport: true
      }} />

      <ImportDialog open={showUpdate} onOpenChange={setShowUpdate} config={{
        title: 'Urun Kartlari Toplu Guncelle',
        templateUrl: '/api/erp/products/export',
        templateDownloadFn: async () => {
          const res = await api.post('/products/export', { ids: [], for_update: true }, { responseType: 'blob' });
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a'); a.href = url; a.download = 'urunler_guncelleme.xlsx'; a.click();
          URL.revokeObjectURL(url);
        },
        previewUrl: '/import/products/update-preview',
        executeUrl: '/import/products/update-execute',
        columns: [
          { key: 'code', label: 'Kod' },
          { key: 'name', label: 'Urun Adi' },
          { key: 'type', label: 'Tur' },
          { key: 'category', label: 'Kategori' },
          { key: 'unit', label: 'Birim' },
          { key: 'changes', label: 'Degisiklikler', render: (item) => (item.changes || []).join(', ') || 'Degisiklik yok' },
        ],
        onComplete: fetchProducts,
        isUpdateMode: true
      }} />
    </div>
  );
}


function MultiFilter({ label, sortKey, sortBy, sortDir, onSort, options, selected, onChange, disabled, testId }) {
  const [open, setOpen] = useState(false);
  const hasFilter = selected.length > 0;
  const isSorted = sortBy === sortKey;

  const toggle = (value) => {
    onChange(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  return (
    <div className="flex items-center gap-0.5">
      {/* Label - click to sort */}
      <button onClick={() => onSort(sortKey)}
        className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-[#3d6e4e] transition-colors ${isSorted ? 'text-[#3d6e4e]' : 'text-[#1a1916]'}`}>
        {label}
        {isSorted && (sortDir === 'asc' ? <ArrowUp size={10} className="inline ml-0.5" /> : <ArrowDown size={10} className="inline ml-0.5" />)}
      </button>

      {/* Filter icon - click to open dropdown */}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button className={`p-0.5 rounded ${hasFilter ? 'text-[#3d6e4e]' : 'text-[#8a7e70] opacity-50 hover:opacity-100'} ${disabled ? 'opacity-20 cursor-not-allowed' : 'cursor-pointer'}`} data-testid={testId}>
            <Filter size={11} />
            {hasFilter && <span className="absolute -top-1 -right-1 bg-[#3d6e4e] text-white rounded-full w-3 h-3 flex items-center justify-center text-[7px]">{selected.length}</span>}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-[#faf7f2] border-[#d0c4b0] max-h-64 overflow-y-auto min-w-[160px]">
          {hasFilter && (
            <DropdownMenuItem onClick={(e) => { e.preventDefault(); onChange([]); }}
              className="text-xs text-[#7a3535] font-semibold focus:bg-[#7a3535]/10 cursor-pointer border-b border-[#d0c4b0] mb-1">
              <X size={12} className="mr-1.5" /> Filtreyi Temizle
            </DropdownMenuItem>
          )}
          {[...options].sort((a, b) => a.label.localeCompare(b.label, 'tr')).map(opt => (
            <DropdownMenuItem key={opt.value} onClick={(e) => { e.preventDefault(); toggle(opt.value); }}
              className="flex items-center gap-2 cursor-pointer text-sm focus:bg-[#e8dfd0]">
              <Checkbox checked={selected.includes(opt.value)} className="h-3.5 w-3.5" />
              <span className="text-[#1a1916]">{opt.label}</span>
            </DropdownMenuItem>
          ))}
          {options.length === 0 && <p className="text-xs text-[#8a7e70] p-2 text-center">Secenek yok</p>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
